// src/features/navigation/DrawerList.jsx
import { Tooltip, List, ListItemButton, ListItemIcon, ListItemText, Box } from "@mui/material"
import React, { useState, useEffect } from "react"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit"

import SettingsIcon from "@mui/icons-material/Settings"
import LayersIcon from "@mui/icons-material/Layers"
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat"
import AirIcon from "@mui/icons-material/Air"
import LogoutIcon from "@mui/icons-material/Logout"
import AltRouteIcon from "@mui/icons-material/AltRoute"
import ShareSharpIcon from "@mui/icons-material/ShareSharp"
import { lighten } from "@mui/material/styles"

import { togglePanel } from "../../reducers/panelsReducer"

export default function DrawerList({
  openPanels,
  dispatch,
  setToogleButtonsOnOff,
  openDrawer,
  setOpenDrawer,
  drawerWidth,
  switchVesselFiltersActive,
  setSwitchVesselFiltersActive,
  openVoyagePlanner,
  switchGisLayersActive,
  setSwitchGisLayersActive,
  setSwitchRnwAnalyserActive,
  openGeolocationList,
  setOpenGeolocationList,
  logout,
  appBarHeight,
  setSwitchVoyagePlannerActive,
  switchRnwAnalyserActive,
  switchVoyagePlannerActive,
  voyageActive,
  setVoyageActive,
  switchMapSettingsActive,
  setSwitchMapSettingsActive,
  switchWeatherForecastActive,
  setSwitchWeatherForecastActive,
  canSeeRnwAnalyser,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false)

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch((err) => {
          console.error("Error attempting fullscreen:", err)
        })
    } else {
      document
        .exitFullscreen()
        .then(() => setIsFullscreen(false))
        .catch((err) => {
          console.error("Error exiting fullscreen:", err)
        })
    }
  }

  // Keep state in sync in case user presses ESC to exit
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
    }
  }, [])

  const handleDrawerOpen = () => {
    setOpenDrawer(true)
  }

  // Panel toggle handlers
  const handleVesselPanel = () => {
    dispatch(togglePanel("vessel"))
    if (!openPanels.vessel) setSwitchVesselFiltersActive(true)
  }

  const handleWeatherPanel = () => {
    dispatch(togglePanel("weather"))
    if (!openPanels.weather) setSwitchWeatherForecastActive(true)
  }

  const handleVoyagePlannerPanel = () => {
    dispatch(togglePanel("voyagePlanner"))
    setVoyageActive(!voyageActive)
    setToogleButtonsOnOff((prev) => {
      if (!openVoyagePlanner) {
        return [...prev, "VoyagePlanner"]
      } else {
        return prev.filter((item) => item !== "VoyagePlanner")
      }
    })
    if (!openPanels.voyagePlanner) setSwitchVoyagePlannerActive(true)
  }

  const handleRnwPanel = () => {
    dispatch(togglePanel("rnw"))
    if (!openPanels.rnw) setSwitchRnwAnalyserActive(true)
  }

  const handleGisPanel = () => {
    dispatch(togglePanel("gis"))
    if (!openPanels.gis) setSwitchGisLayersActive(true)
  }

  const handleMapPanel = () => {
    dispatch(togglePanel("map"))
    if (!openPanels.map) setSwitchMapSettingsActive(true)
  }

  const handleGeolocationList = () => {
    setOpenGeolocationList(!openGeolocationList)
    if (!openDrawer) {
      handleDrawerOpen()
    }
  }

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", height: `calc(100vh - ${appBarHeight}px)` }}>
        <Box sx={{ flexGrow: 1 }}>
          <List
            sx={{
              width: "100%",
              minWidth: drawerWidth,
              maxWidth: drawerWidth,
              bgcolor: "background.paper",
              pt: 0,
              pb: 0,
              "& .MuiListItemIcon-root": {
                minWidth: 56,
              },
            }}
            component="nav"
            aria-labelledby="drawer-list"
          >
            {/* Fleet Explorer */}
            <ListItemButton
              onClick={handleVesselPanel}
              sx={{
                backgroundColor: switchVesselFiltersActive ? lighten("#004f7d", 0.3) : "#004f7d",
                "&:hover": {
                  backgroundColor: lighten("#004f7d", 0.3),
                },
              }}
            >
              <ListItemIcon data-tour="drawer-fleet">
                <Tooltip title="Fleet Explorer" placement="right">
                  <DirectionsBoatIcon sx={{ color: "white" }} />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary="Fleet Explorer" sx={{ color: "white" }} />
            </ListItemButton>
            

            {/* Voyage Planner */}
            <ListItemButton
              onClick={handleVoyagePlannerPanel}
              sx={{
                backgroundColor: switchVoyagePlannerActive ? lighten("#004f7d", 0.3) : "#004f7d",
                "&:hover": {
                  backgroundColor: lighten("#004f7d", 0.3),
                },
              }}
            >
              <ListItemIcon data-tour="drawer-planner">
                <Tooltip title="Voyage Planner" placement="right">
                  <AltRouteIcon sx={{ color: "white" }} />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary="Voyage Planner" sx={{ color: "white" }} />
            </ListItemButton>
            
            {/* Weather Forecast */}
            <ListItemButton
              onClick={handleWeatherPanel}
              sx={{
                backgroundColor: switchWeatherForecastActive ? lighten("#004f7d", 0.3) : "#004f7d",
                "&:hover": {
                  backgroundColor: lighten("#004f7d", 0.3),
                },
              }}
            >
              <ListItemIcon data-tour="drawer-weather">
                <Tooltip title="Weather Forecast" placement="right">
                  <AirIcon sx={{ color: "white" }} />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary="Weather Forecast" sx={{ color: "white" }} />
            </ListItemButton>

            {/* RNW Analyser */}
            {canSeeRnwAnalyser ? (
              <ListItemButton
                onClick={handleRnwPanel}
                sx={{
                  backgroundColor: switchRnwAnalyserActive ? lighten("#004f7d", 0.3) : "#004f7d",
                  "&:hover": {
                    backgroundColor: lighten("#004f7d", 0.3),
                  },
                }}
              >
                <ListItemIcon>
                  <Tooltip title="RNW Analyser" placement="right">
                    <ShareSharpIcon sx={{ color: "white" }} />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText primary="RNW Analyser" sx={{ color: "white" }} />
              </ListItemButton>
            ) : null}

            {/* GIS Layers */}
            <ListItemButton
              onClick={handleGisPanel}
              sx={{
                backgroundColor: switchGisLayersActive ? lighten("#004f7d", 0.3) : "#004f7d",
                "&:hover": {
                  backgroundColor: lighten("#004f7d", 0.3),
                },
              }}
            >
              <ListItemIcon data-tour="drawer-gis">
                <Tooltip title="GIS Layers" placement="right">
                  <LayersIcon sx={{ color: "white" }} />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary="GIS Layers" sx={{ color: "white" }} />
            </ListItemButton>

            {/* Map Settings */}
            <ListItemButton
              onClick={handleMapPanel}
              sx={{
                backgroundColor: switchMapSettingsActive ? lighten("#004f7d", 0.3) : "#004f7d",
                "&:hover": {
                  backgroundColor: lighten("#004f7d", 0.3),
                },
              }}
            >
              <ListItemIcon data-tour="drawer-map">
                <Tooltip title="Map Settings" placement="right">
                  <SettingsIcon sx={{ color: "white" }} />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary="Map Settings" sx={{ color: "white" }} />
            </ListItemButton>

            {/* 
          <ListItemButton onClick={handleGeolocationList}>
            <ListItemIcon>
              <PublicIcon sx={{ color: "#004f7d" }} />
            </ListItemIcon>
            <ListItemText primary="Geolocation" />
            {openGeolocationList ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={openGeolocationList} timeout="auto" unmountOnExit>
            <ListItemButton sx={{ pl: 4, color: "primary" }}>
              <GeolocationWatcher />
            </ListItemButton>
          </Collapse> */}
          </List>
        </Box>

        <Box sx={{ bottom: 0 }}>
          <List
            sx={{
              width: "100%",
              minWidth: drawerWidth,
              maxWidth: drawerWidth,
              pb: 0,
              "& .MuiListItemIcon-root": {
                minWidth: 56,
              },
            }}
          >
            {/* Fullscreen */}
            <ListItemButton
              onClick={toggleFullscreen}
              sx={{
                backgroundColor: "#004f7d",
                "&:hover": { backgroundColor: lighten("#004f7d", 0.3) },
              }}
            >
              <ListItemIcon>
                <Tooltip title="Toggle Fullscreen" placement="right">
                  {isFullscreen ? (
                    <FullscreenExitIcon sx={{ color: "white" }} />
                  ) : (
                    <FullscreenIcon sx={{ color: "white" }} />
                  )}
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary="Fullscreen" sx={{ color: "white" }} />
            </ListItemButton>

            {/* Logout */}
            <ListItemButton
              onClick={logout}
              sx={{
                backgroundColor: "#004f7d",
                "&:hover": {
                  backgroundColor: lighten("#004f7d", 0.3),
                },
              }}
            >
              <ListItemIcon>
                <Tooltip title="Logout" placement="right">
                  <LogoutIcon sx={{ color: "white" }} />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary="Logout" sx={{ color: "white" }} />
            </ListItemButton>
          </List>
        </Box>
      </Box>
    </>
  )
}
